/* ─────────────────────────────────────────────────────────────
   FCFE BUILD — HISTORICAL & PROJECTED (continuous)

   TRANSPOSED (2026-09-09): line items are ROWS and fiscal years are COLUMNS,
   matching the FCFE-build block in the reference note. The series is still one
   unbroken left→right run: `fcfe_history` years (actuals) followed directly by
   `intrinsic_value.projected_fcfe` years. For AAPL that is FY2018–2025 actuals
   → FY2026–2030 projected.

   Rows are engine-dependent (see fcfeRowsFor) and a year simply renders "—" in
   a line item it does not carry. Only the build line items are shown — the
   model's working columns (anchored OCF/NI, D&A, Δ WC, t (mid)) are received
   but not rendered.

   WHICH YEAR IS "CURRENT": each payload row carries its own `is_projected`,
   and it is authoritative — the array a row arrived in is NOT. The last year
   with `is_projected === false` is the current fiscal year and is tagged as
   such; everything after it is tagged PROJ and tinted, with the divider drawn
   at that boundary.

   One disclosure this reproduces from the artifact:
     • net_borrowing_raw_unclamped — when present the value was clamped, so the
       cell is flagged and the raw number shown on hover.
───────────────────────────────────────────────────────────── */

import React from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { formatCurrency, fmtL } from "../../../utils/currency";
import { fcfeRowsFor, decimalAsPct } from "../../../config/valuation";
import { SectionTitle, Scroller, Table, Th, Td, Tl, Banner } from "./earningsNoteUI";

const cellValue = (kind, v) => {
  if (v == null) return "—";
  if (kind === "pct") return decimalAsPct(v, 2);
  if (kind === "years") return fmtL(v, 3);
  if (kind === "mult") return `${fmtL(v, 2)}×`;
  return formatCurrency(v, { compact: true, decimals: 2 });
};

const PROJ_TINT = "rgba(0,150,255,0.05)";
const BOUNDARY = "2px solid var(--color-primary)";

const FcfeTable = ({ history = [], projected = [], method }) => {
  /* One continuous series of fiscal years. `is_projected` is taken from the
     row itself whenever the payload states it — the intrinsic-value response
     can (and does) return a not-yet-projected year inside `projected_fcfe`. */
  const periods = React.useMemo(
    () =>
      [
        ...history.map((r) => ({ ...r, is_projected: r?.is_projected ?? false })),
        ...projected.map((r) => ({ ...r, is_projected: r?.is_projected ?? true })),
      ].sort((a, b) => (Number(a?.fiscal_year) || 0) - (Number(b?.fiscal_year) || 0)),
    [history, projected]
  );

  const rows = React.useMemo(() => fcfeRowsFor(method, periods), [method, periods]);

  if (periods.length === 0 || rows.length === 0) return null;

  /* Current = the LAST actual year, i.e. the last `is_projected === false`. */
  let currentIdx = -1;
  periods.forEach((p, i) => {
    if (!p.is_projected) currentIdx = i;
  });
  const firstProjectedIdx = periods.findIndex((p) => p.is_projected);
  const anyClamped = periods.some((p) => p?.net_borrowing_raw_unclamped != null);

  /* Left border marks the actual → projected boundary. */
  const boundaryStyle = (i) =>
    i === firstProjectedIdx && firstProjectedIdx > 0 ? { borderLeft: BOUNDARY } : null;

  return (
    <>
      <SectionTitle caption="actual fiscal years followed by the projected path — one continuous series">
        FCFE build
      </SectionTitle>

      <Scroller>
        <Table>
          <thead>
            <tr>
              <Th left style={{ position: "sticky", left: 0, zIndex: 3 }}>
                FCFE build
              </Th>
              {periods.map((p, i) => (
                <Th
                  key={`${p?.fiscal_year}-${i}`}
                  style={{
                    backgroundColor: p.is_projected ? PROJ_TINT : undefined,
                    ...boundaryStyle(i),
                  }}
                >
                  <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <span>FY{p?.fiscal_year ?? "—"}</span>
                    {p.is_projected ? (
                      <span
                        style={{
                          color: "var(--color-primary)",
                          fontSize: 9,
                          fontWeight: "var(--font-weight-bold)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        PROJ
                      </span>
                    ) : i === currentIdx ? (
                      <span
                        style={{
                          color: "var(--color-warning)",
                          fontSize: 9,
                          fontWeight: "var(--font-weight-bold)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        CURRENT
                      </span>
                    ) : null}
                  </span>
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <Tl
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: row.emphasis
                      ? "var(--color-surface-hover)"
                      : "var(--color-surface)",
                    fontWeight: row.emphasis
                      ? "var(--font-weight-bold)"
                      : "var(--font-weight-semibold)",
                    borderTop: row.emphasis
                      ? "1.5px solid var(--color-border-secondary)"
                      : undefined,
                  }}
                >
                  {row.label}
                  {row.note && (
                    <span
                      style={{
                        marginLeft: 6,
                        color: "var(--color-text-muted)",
                        fontSize: 10,
                        fontWeight: 500,
                      }}
                    >
                      {row.note}
                    </span>
                  )}
                </Tl>

                {periods.map((p, i) => {
                  const v = p[row.key];
                  const clamped =
                    row.key === "net_borrowing" && p.net_borrowing_raw_unclamped != null;
                  const body = cellValue(row.kind, v);

                  return (
                    <Td
                      key={`${row.key}-${i}`}
                      bold={row.emphasis}
                      tone={p.is_projected ? "info" : "neutral"}
                      style={{
                        backgroundColor: row.emphasis
                          ? "var(--color-surface-hover)"
                          : p.is_projected
                          ? PROJ_TINT
                          : undefined,
                        borderTop: row.emphasis
                          ? "1.5px solid var(--color-border-secondary)"
                          : undefined,
                        ...boundaryStyle(i),
                      }}
                    >
                      {clamped ? (
                        <MuiTooltip
                          title={`Clamped from ${formatCurrency(p.net_borrowing_raw_unclamped, {
                            compact: true,
                          })}`}
                          placement="top"
                          arrow
                        >
                          <span style={{ cursor: "help", textDecoration: "underline dotted" }}>
                            {body}
                            <span style={{ color: "var(--color-warning)" }}> ⚑</span>
                          </span>
                        </MuiTooltip>
                      ) : (
                        body
                      )}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroller>

      {anyClamped && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-sm)" }}>
          Net borrowing was clamped in the flagged years — hover a ⚑ cell to see the
          unclamped model value.
        </Banner>
      )}
    </>
  );
};

export default FcfeTable;
